import { useEffect, useState } from 'react'

import { ChevronLeft, LayoutSideContent } from '@gravity-ui/icons'
import { Button, Tooltip } from '@heroui/react'
import { AnimatePresence, motion } from 'framer-motion'
import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

import { useAuthStore } from '@/store/auth.store'
import { useLayoutStore } from '@/store/layout.store'

interface PageHeaderProps {
  title: ReactNode;
  subtitle?: ReactNode;
  onBack?: () => void;
  actions?: ReactNode;
}

export function PageHeader({ title, subtitle, onBack, actions }: PageHeaderProps) {
  const { t } = useTranslation()
  const toggleSidebar = useLayoutStore(s => s.toggleSidebar)
  const { user, showGreeting, setShowGreeting } = useAuthStore()
  const [randomPhraseIndex] = useState(() => Math.floor(Math.random() * 5))

  useEffect(() => {
    if (showGreeting) {
      const timer = setTimeout(() => {
        setShowGreeting(false)
      }, 3000)
      return () => {
        clearTimeout(timer)
        setShowGreeting(false)
      }
    }
  }, [showGreeting, setShowGreeting])

  return (
    <div className="flex items-center gap-4">
      <Tooltip delay={0}>
        <Button
          isIconOnly
          variant="ghost"
          size="sm"
          aria-label="Toggle sidebar"
          onPress={toggleSidebar}
        >
          <LayoutSideContent width={18} height={18} />
        </Button>
        <Tooltip.Content>
          <p>{t('common.toggle_sidebar')}</p>
        </Tooltip.Content>
      </Tooltip>

      {onBack && (
        <Tooltip delay={0}>
          <Button
            isIconOnly
            variant="ghost"
            size="sm"
            aria-label={t('common.back')}
            onPress={onBack}
          >
            <ChevronLeft width={18} height={18} />
          </Button>
          <Tooltip.Content>
            <p>{t('common.back')}</p>
          </Tooltip.Content>
        </Tooltip>
      )}

      <div className="flex-1 min-w-0">
        <AnimatePresence mode="wait">
          {showGreeting && user ? (
            <motion.div
              key="greeting"
              initial={{ opacity: 0, y: 4 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -4 }}
              transition={{ duration: 0.2 }}
            >
              <h1 className="text-xl font-bold tracking-tight text-zinc-800 dark:text-zinc-100 flex items-center gap-2">
                {t('common.good_day', { name: user.Name })}
              </h1>
              <p className="text-sm mt-0.5 truncate text-zinc-600 dark:text-zinc-400">
                {t(`common.greeting_sub_${randomPhraseIndex + 1}`)}
              </p>
            </motion.div>
          ) : (
            <motion.div
              key="title-subtitle"
              initial={{ opacity: 0, y: 4 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -4 }}
              transition={{ duration: 0.2 }}
            >
              <h1 className="text-xl font-bold tracking-tight truncate text-zinc-800 dark:text-zinc-100">
                {title}
              </h1>
              {subtitle && (
                <p className="text-sm mt-0.5 truncate text-zinc-600 dark:text-zinc-400">
                  {subtitle}
                </p>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {actions && (
        <div className="flex items-center gap-2 ml-auto">
          {actions}
        </div>
      )}
    </div>
  )
}
